import React from 'react';
import { GraduationCap, Heart, Zap, MapPin, Coffee, Gamepad2, Monitor, Moon, Code, Tv, Music, Skull } from 'lucide-react';

const hobbyIcons = {
    'Sleeping': Moon,
    'Coding': Code,
    'Anime': Tv,
    'Genshin': Gamepad2,
    'Project Sekai': Music,
    'Being a menace to society': Skull,
    'Pokemon TCG': Gamepad2,
};

const AboutSection = ({ education, hobbies }) => {
  return (
    <section id="about" className="py-20 md:py-32 relative">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-display mb-4 text-white tracking-wide">
            About Me
          </h2>
          <p className="text-white/40 max-w-2xl mx-auto font-mono text-sm">
            A little bit about who I am and what I love to do
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Education Card */}
          <div className="card-cute group">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-white/5 text-white/70">
                <GraduationCap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white">Education</h3>
            </div>

            {education.map((edu, index) => (
              <div key={index} className="pl-4 border-l-2 border-white/10">
                <h4 className="text-lg font-semibold text-white">{edu.institution}</h4>
                <p className="text-white/70 mt-1">{edu.degree}</p>
                <p className="text-white/40 text-sm mt-1">{edu.years}</p>
              </div>
            ))}

            {/* Decorative element */}
            <div className="absolute top-4 right-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <GraduationCap className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Hobbies Card */}
          <div className="card-cute group">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-white/5 text-white/70">
                <Heart className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white">Things I Love</h3>
            </div>

            <div className="flex flex-wrap gap-3">
              {hobbies.map((hobby, index) => {
                const IconComponent = hobbyIcons[hobby] || Heart;
                return (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/8 text-white/70 text-sm font-medium hover:border-white/20 hover:text-white hover:scale-105 transition-all duration-300 cursor-default"
                  >
                    <IconComponent className="w-4 h-4 text-white/50" />
                    {hobby}
                  </span>
                );
              })}
            </div>

            {/* Decorative element */}
            <div className="absolute top-4 right-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Heart className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>

        {/* Fun Facts */}
        <div className="mt-12 card-cute">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-white/5 text-white/70">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white">Quick Facts</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FactCard icon={MapPin} label="Based in" value="Sydney" />
            <FactCard icon={Coffee} label="Fuel" value="Monster" />
            <FactCard icon={Gamepad2} label="Currently Playing" value="Genshin Impact" />
            <FactCard icon={Monitor} label="Editor" value="VS Code" />
          </div>
        </div>
      </div>
    </section>
  );
};

const FactCard = ({ icon: Icon, label, value }) => (
  <div className="p-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] transition-colors group cursor-default border border-white/5">
    <div className="mb-2 text-white/50 group-hover:text-white/70 transition-colors">
      <Icon className="w-6 h-6" />
    </div>
    <p className="text-white/40 text-xs uppercase tracking-wider">{label}</p>
    <p className="text-white font-medium mt-1">{value}</p>
  </div>
);

export default AboutSection;
